import React, { useState, useEffect, useMemo, useRef } from "react";
import { Select, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { fetchBrandsBySelectedClients } from "../core/api/settings";
import { setSelectedBrands } from "../core/redux/action";

const BrandSelector = () => {
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const dispatch = useDispatch();
  const selectedClients = useSelector((state) => state.selectedClients);
  const selectedBrands = useSelector((state) => state.selectedBrands || []);
  // Keep a ref in sync with Redux so we can read the latest selection
  // inside effects without re-running them unnecessarily.
  const selectedBrandsRef = useRef(selectedBrands);

  const normalizedClientSelection = useMemo(() => {
    if (!Array.isArray(selectedClients)) {
      return {
        ids: [],
        cacheKey: "",
      };
    }

    const normalizedIds = selectedClients
      .map((id) => String(id).trim())
      .filter((id) => id.length > 0);

    if (normalizedIds.length === 0) {
      return {
        ids: [],
        cacheKey: "",
      };
    }

    const uniqueSortedIds = Array.from(new Set(normalizedIds)).sort((a, b) =>
      a.localeCompare(b)
    );

    return {
      ids: uniqueSortedIds,
      cacheKey: uniqueSortedIds.join(","),
    };
  }, [selectedClients]);

  const lastClientCacheKeyRef = useRef(null);

  // Keep the ref updated when selectedBrands changes
  useEffect(() => {
    selectedBrandsRef.current = selectedBrands;
  }, [selectedBrands]);

  useEffect(() => {
    const { ids, cacheKey } = normalizedClientSelection;

    // Prevent duplicate fetches for the same client selection
    if (cacheKey === lastClientCacheKeyRef.current) {
      return;
    }

    // Update ref immediately to avoid race conditions with rapid updates
    lastClientCacheKeyRef.current = cacheKey;

    setLoadingBrands(true);

    fetchBrandsBySelectedClients(ids)
      .then((res) => {
        const responseData = res?.data ?? [];

        if (!Array.isArray(responseData) || responseData.length === 0) {
          setBrands([]);
          // No brands available for this client selection; clear selection.
          dispatch(setSelectedBrands([]));
          setLoadingBrands(false);
          return;
        }

        setBrands(responseData);

        // Preserve any existing brand selection that is still valid
        // for the newly loaded list of brands.
        const currentSelection = selectedBrandsRef.current || [];
        const validSelection = currentSelection.filter((brand) =>
          responseData.some((b) => b.id === brand.id)
        );

        let newSelection;
        if (validSelection.length > 0) {
          // Keep the user's previous selection intact.
          newSelection = validSelection;
        } else {
          // If there is no existing selection, default to ALL brands for this client.
          // This restores the previous behaviour where a single client selection
          // automatically showed all their brands on accounting pages.
          newSelection = responseData;
        }
        
        // Dispatch custom event BEFORE updating Redux to notify components to clear cache
        window.dispatchEvent(new CustomEvent('globalBrandChanged', { 
          detail: { brandIds: newSelection.map(b => b.id), brands: newSelection } 
        }));
        dispatch(setSelectedBrands(newSelection));
        // Save to localStorage for persistence
        localStorage.setItem("selectedBrands", JSON.stringify(newSelection));

        setLoadingBrands(false);
      })
      .catch(() => {
        setLoadingBrands(false);
        message.error("Failed to fetch brands.");
      });
  }, [normalizedClientSelection, dispatch]);

  return (
    <div className="brand-selector">
      <Select
        mode="multiple"
        allowClear
        placeholder="Select Brand(s)"
        options={brands.map((brand) => ({
          value: brand.id,
          label: brand.brand_name,
        }))}
        onChange={(values) => {
          const selectedBrandObjs = brands.filter((brand) =>
            values.includes(brand.id)
          );
          // Dispatch custom event BEFORE updating Redux to notify components to clear cache
          window.dispatchEvent(new CustomEvent('globalBrandChanged', { 
            detail: { brandIds: selectedBrandObjs.map(b => b.id), brands: selectedBrandObjs } 
          }));
          dispatch(setSelectedBrands(selectedBrandObjs));
          // Save to localStorage for persistence
          localStorage.setItem("selectedBrands", JSON.stringify(selectedBrandObjs));
        }}
        value={selectedBrands.map((brand) => brand.id)}
        loading={loadingBrands}
        style={{ width: 200 }}
        maxTagCount="responsive"
        tagRender={(props) => {
          const validSelections = selectedBrands.filter((brand) =>
            brands.some((b) => b.id === brand.id)
          );
          const isFirstTag = props.value === validSelections[0]?.id;
          if (validSelections.length === brands.length && isFirstTag) {
            return <span style={{ padding: "0 8px" }}>All Brands</span>;
          }
          if (validSelections.length > 1 && isFirstTag) {
            const firstBrand = brands.find(
              (b) => b.id === validSelections[0]?.id
            );
            return (
              <span style={{ padding: "0 8px" }}>
                {firstBrand?.brand_name || "Brand"} +{" "}
                {validSelections.length - 1}
              </span>
            );
          }
          if (validSelections.length === 1 && isFirstTag) {
            const brand = brands.find((b) => b.id === validSelections[0]?.id);
            return (
              <span style={{ padding: "0 8px" }}>
                {brand?.brand_name || "Brand"}
              </span>
            );
          }
          return null;
        }}
        dropdownRender={(menu) => (
          <>
            <div
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #f0f0f0",
                color: "#fd7e14",
                fontWeight: 500,
              }}
              onClick={() => {
                const allIds = brands.map((b) => b.id);
                const isAllSelected = selectedBrands.length === allIds.length;

                const newSelection = isAllSelected ? [] : brands;

                // Dispatch custom event BEFORE updating Redux
                window.dispatchEvent(new CustomEvent('globalBrandChanged', { 
                  detail: { brandIds: newSelection.map(b => b.id), brands: newSelection } 
                }));
                dispatch(setSelectedBrands(newSelection));
                // Save to localStorage for persistence
                localStorage.setItem("selectedBrands", JSON.stringify(newSelection));
              }}
            >
              {selectedBrands.length === brands.length
                ? "Deselect All"
                : "Select All"}
            </div>
            {menu}
          </>
        )}
      />
    </div>
  );
};

export default BrandSelector;
